/* eslint-disable max-len */
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useObserverReturningStore } from '../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../concepts/resources/Icon';
import { LabelValue } from '../../platform/types/LabelValue';
import { Button, Colors, Toggle } from '../../ui';
import { DropButton } from '../../ui/DropButton';
import { TextButton } from '../../ui/TextButton';
import { useResolve } from '../Container';
import { Flexer, Title, ToggleFrame } from '../dashboard/elements/LayoutElements';
import { GridSystem } from './GridSystem';

const Label = styled.label`
    flex: 1 1 auto;
    color: ${Colors.text};
`;

const Input = styled.input`
    padding: .125rem .375rem;
    width: 100%;
    border: solid 1px ${Colors.border};
    border-radius: .25rem;
    background: white;
`;

const Select = styled.select`
    padding: .125rem;
    width: 100%;
    border: solid 1px ${Colors.border};
    border-radius: .25rem;
    background: white;
`;

const InputAcceptMount = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    right: .25rem;
    bottom: 0;    
`;

const Stack = styled.div`
    display: grid;
    grid-auto-rows: 1fr;
    row-gap: .5rem;
    border-top: solid 1px ${Colors.border};
    padding: .75rem 1rem;
`;

const TextInput = React.memo<{ label: string, value: string, onApply: (newValue: string) => void }>(({ label, value, onApply }) => {

    const [next, setNext] = useState(value.toString());
    const onSave = useCallback(() => onApply(next), [next, onApply]);
    const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onSave();
        }
    }, [onSave]);

    return <Flexer flex="row" align="center">
        <Label>{label}</Label>
        <Flexer relative shrink width="8">
            <Input spellCheck={false} value={next} onChange={e => setNext(e.target.value)} onKeyDown={onKeyDown} />
            {next !== value.toString() && <InputAcceptMount>
                <DropButton overflow="0" padding=".125" onMouseUp={onSave}>
                    <Icon size="1.25" src="deqo://icons/hero/check" />
                </DropButton>
            </InputAcceptMount>}
        </Flexer>
    </Flexer>;
});

type OptionInputProps = {
    label: string,
    options: LabelValue[],
    value: any,
    onApply: (newValue: number) => void,
};

const OptionInput = React.memo<OptionInputProps>(({ label, options, value, onApply }) => {

    return <Flexer flex="row" align="center">
        <Label>{label}</Label>
        <Flexer relative shrink width="8">
            <Select value={value} onChange={e => onApply(e.target.value as any)}>
                {options.map(x =>
                    <option key={x.value} value={x.value}>
                        {x.label}
                    </option>
                )}
            </Select>
        </Flexer>
    </Flexer>;
});

const ToggleInput = React.memo<{ label: string, value: boolean, onApply: (newValue: boolean) => void }>(({ label, value, onApply }) => {
    return <Flexer flex="row" align="center">
        <Label>{label}</Label>
        <Flexer relative shrink width="8">
            <ToggleFrame>
                <Toggle size="sm" value={value} onAction={onApply} />
            </ToggleFrame>
        </Flexer>
    </Flexer>;
});

const ButtonInput = React.memo<{ label: string, onApply: () => void }>(({ label, onApply }) => {
    return <Flexer flex="row" align="center">
        <Label></Label>
        <Flexer relative shrink width="8">
            <Button size="sm" onAction={onApply}>{label}</Button>
        </Flexer>
    </Flexer>;
});

const safeParseLines = (value: string) => {
    const [_, val] = value.match(/(\d+)/);
    return parseInt(val) || 10;
};

export const GridSettingsWidget = () => {

    const grid = useObserverReturningStore(useResolve(GridSystem));

    const [modeOpts] = useState(() => [
        LabelValue('Dots & Lines', 'dots-lines'),
        LabelValue('Just Dots', 'dots'),
        LabelValue('Off', 'off'),
    ]);

    return <Flexer width="20">
        <Flexer padding=".5 1" background={Colors.gray50}>
            <Title flush size="2xs" color={Colors.text} uppercase weight="500">Grid Settings</Title>
        </Flexer>
        <Stack>
            <OptionInput label="Grid Mode:" options={modeOpts} value={grid.mode} onApply={x => grid.configure({ mode: x as any })} />
            <TextInput label="Line Size:" value={grid.lines.toString()} onApply={x => grid.configure({ lines: safeParseLines(x) })} />
            <ToggleInput label="Snap to Grid:" value={grid.gridSnap} onApply={x => grid.configure({ gridSnap: x })} />
            <ToggleInput label="Snap to Guides:" value={grid.guideSnap} onApply={x => grid.configure({ guideSnap: x })} />
        </Stack>
        <Stack>
            <Flexer>
                <TextButton style={{ fontSize: '.875rem' }} onClick={() => grid.reset()}>Reset to defaults</TextButton>
            </Flexer>
        </Stack>
    </Flexer>;
};
